import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { calculateTax, formatPriceWithTax } from '../../utils/taxCalculator';

/**
 * Компонент для отображения цены с налогами
 * @param {Object} props - Свойства компонента
 * @param {number} props.basePrice - Базовая цена без налогов
 * @param {Object} props.user - Данные пользователя для расчета налогов
 * @param {boolean} props.showBreakdown - Показать разбивку цены
 * @param {string} props.variant - Вариант отображения (default, compact, detailed)
 * @param {Object} props.sx - Стили Material-UI
 * @returns {JSX.Element} Компонент цены с налогами
 */
const PriceWithTax = ({ 
  basePrice, 
  user, 
  showBreakdown = false, 
  variant = 'default',
  sx = {} 
}) => {
  // Если нет цены, показываем Free
  if (!basePrice || basePrice <= 0) {
    return (
      <Typography variant="h5" sx={sx}>
        Free
      </Typography>
    );
  }

  // Подготавливаем данные клиента для расчета налогов
  const customerData = {
    billingCountry: user?.billingCountry || '',
    billingHasVat: user?.billingHasVat || false,
    billingVatNumber: user?.billingVatNumber || '',
    isCompany: user?.billingHasVat && user?.billingVatNumber // Считаем компанией если есть VAT
  };

  // Рассчитываем налоги
  const taxCalculation = calculateTax(customerData, basePrice);
  const formattedPrice = formatPriceWithTax(taxCalculation);

  // Компактный вариант - только итоговая цена
  if (variant === 'compact') {
    return (
      <Tooltip title={taxCalculation.shouldApplyTax ? 
        `Base: ${formattedPrice.breakdown.base}, Tax: ${formattedPrice.breakdown.tax}` : 
        'No tax applied'
      }>
        <Typography variant="h5" sx={sx}>
          {formattedPrice.totalAmountFormatted}
          {taxCalculation.shouldApplyTax && (
            <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              incl. VAT
            </Typography>
          )}
        </Typography>
      </Tooltip>
    );
  }

  // Детальный вариант - полная разбивка
  if (variant === 'detailed') {
    return (
      <Box sx={sx}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {formattedPrice.totalAmountFormatted}
        </Typography>
        <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          <Typography variant="body2">
            Base price: {formattedPrice.breakdown.base}
          </Typography>
          <Typography variant="body2">
            Tax: {formattedPrice.breakdown.tax}
            {taxCalculation.shouldApplyTax && (
              <span> ({taxCalculation.taxRate}%)</span>
            )}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 0.5 }}>
            Total: {formattedPrice.breakdown.total}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Обычный вариант - цена с подсказкой
  return (
    <Box sx={sx}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        {formattedPrice.totalAmountFormatted}
      </Typography>
      {showBreakdown && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
          {formattedPrice.taxInfoText}
        </Typography>
      )}
      {!showBreakdown && taxCalculation.shouldApplyTax && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          incl. VAT
        </Typography>
      )}
    </Box>
  );
};

export default PriceWithTax; 